import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRecordPropertyTaxPayment } from '@/hooks/usePropertyTax';
import { useFetchAccounts } from '@accountHooks';
import { useFetchCategories } from '@metadataHooks';
import { useFetchSubCategories } from '@metadataHooks';
import { useCreateTransaction } from '@/hooks/useTransactions';
import { ConversionPreview } from '@/components/conversion-preview/ConversionPreview';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/currency.utils';
import { Skeleton } from '../../../../components/ui/skeleton';

const schema = z.object({
  year_paid: z.number().min(1900).max(2100),
  amount: z.number().min(0.01, 'Amount must be greater than 0'),
  payment_date: z.string().min(1, 'Payment date is required'),
  account_id: z.string().optional(),
  receipt_number: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});

type FormData = z.infer<typeof schema>;

interface PropertyTaxPaymentFormProps {
  propertyTaxId: string;
  paymentCurrency: string;
  defaultYear?: number;
  expectedAmount?: number;
  onSuccess: () => void;
}

export function PropertyTaxPaymentForm({ propertyTaxId, paymentCurrency, defaultYear, expectedAmount, onSuccess }: PropertyTaxPaymentFormProps) {
  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      year_paid: defaultYear || new Date().getFullYear(),
      amount: expectedAmount || 0,
      payment_date: new Date().toISOString().split('T')[0],
      account_id: '',
      receipt_number: '',
      notes: '',
    }
  });

  const amount = watch('amount');
  const accountId = watch('account_id');

  const { data: accounts = [], isLoading: accountsLoading } = useFetchAccounts();
  const selectedAccount = accounts.find((a) => a.id === accountId);

  const { data: categories = [] } = useFetchCategories();
  const taxCategory = categories.find((c) => c.name.toLowerCase() === 'tax');
  const { data: subCategories = [] } = useFetchSubCategories(taxCategory?.id);
  const propertySubCat = subCategories.find((sc) => sc.name.toLowerCase() === 'property');

  const recordPayment = useRecordPropertyTaxPayment();
  const createTransaction = useCreateTransaction();

  const onSubmit = async (data: FormData) => {
    try {
      let transactionId = null;

      if (data.account_id) {
        if (!taxCategory) {
          throw new Error('Tax category not found. Please create it first.');
        }

        const tx = await createTransaction.mutateAsync({
          account_id: data.account_id,
          type: 'expense',
          amount: data.amount,
          transaction_date: data.payment_date,
          category_id: taxCategory.id,
          sub_category_id: propertySubCat?.id || null,
          currency: paymentCurrency,
          description: `Property Tax Payment for Year ${data.year_paid}`,
          notes: data.notes || null,
          fee: 0,
        });
        transactionId = tx.id;
      }

      await recordPayment.mutateAsync({
        property_tax_id: propertyTaxId,
        year_paid: data.year_paid,
        amount: data.amount,
        payment_date: data.payment_date,
        receipt_number: data.receipt_number || null,
        notes: data.notes || null,
        transaction_id: transactionId,
      });
      toast.success('Payment recorded successfully');
      onSuccess();
    } catch (error: any) {
      if (error.code === '23505') {
        toast.error(`A payment for year ${data.year_paid} has already been recorded.`);
      } else {
        toast.error(`Failed to record payment: ${error.message}`);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="year_paid">For Year</Label>
          <Input
            id="year_paid"
            type="number"
            {...register('year_paid', { valueAsNumber: true })}
          />
          {errors.year_paid && <p className="text-sm text-destructive">{errors.year_paid.message}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="amount">Amount Paid</Label>
          <Input
            id="amount"
            type="number"
            step="0.01"
            {...register('amount', { valueAsNumber: true })}
          />
          {errors.amount && <p className="text-sm text-destructive">{errors.amount.message}</p>}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="payment_date">Payment Date</Label>
        <Input
          id="payment_date"
          type="date"
          {...register('payment_date')}
        />
        {errors.payment_date && <p className="text-sm text-destructive">{errors.payment_date.message}</p>}
      </div>

      <div className="space-y-2">
        <Label>Debit Account (Optional)</Label>
        <Select
          onValueChange={(val) => setValue('account_id', val === 'none' ? '' : val)}
          value={accountId || 'none'}
          disabled={accountsLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select an account to debit" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Do not debit an account</SelectItem>
            {accounts.map((acc) => (
              <SelectItem key={acc.id} value={acc.id}>
                {acc.nickname || acc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {accountsLoading && accountId ? (
          <Skeleton className="h-4 w-32" />
        ) : selectedAccount?.currency ? (
          <p className="text-sm text-muted-foreground">
            {formatCurrency(selectedAccount.balance, selectedAccount.currency)}
          </p>
        ) : null}
        {selectedAccount && amount > 0 && selectedAccount.currency !== paymentCurrency && (
          <div className="mt-2">
            <ConversionPreview
              amount={amount}
              fromCurrency={paymentCurrency}
              toCurrency={selectedAccount.currency}
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="receipt_number">Receipt Number (Optional)</Label>
        <Input
          id="receipt_number"
          {...register('receipt_number')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes (Optional)</Label>
        <Input
          id="notes"
          {...register('notes')}
        />
      </div>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting || createTransaction.isPending}>
          {isSubmitting || createTransaction.isPending ? 'Recording...' : 'Record Payment'}
        </Button>
      </DialogFooter>
    </form>
  );
}
